import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import { CAPABILITY_CHECKED_AT_KEY, parseJsonStringObject, type OrdatConnectionTestResult } from './types.ts';
import { Alert } from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';

const OkIcon = withIcon(faCheck);
const FailedIcon = withIcon(faXmark);

export interface OrdatConnectionTestModalProps {
    integrationName: string;
    result: OrdatConnectionTestResult;
    onClose: () => void;
}

const CapabilityList = ({ capabilities }: { capabilities: Record<string, any> }) => {
    const entries = Object.entries(capabilities)
        .filter(([key]) => key !== CAPABILITY_CHECKED_AT_KEY)
        .sort(([a], [b]) => a.localeCompare(b));

    if (entries.length === 0) return null;

    return (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {entries.map(([key, allowed]) => (
                <li key={key} className="flex gap-2 items-center text-sm">
                    <span className={allowed ? 'text-content-success-strong' : 'text-content-danger-strong'}>
                        {allowed ? <OkIcon /> : <FailedIcon />}
                    </span>
                    <code className="text-content-strong">{key}</code>
                </li>
            ))}
        </ul>
    );
};

/** Result of `testOrdatIntegrationConnection`: verdict, messages and the capability checklist. */
const OrdatConnectionTestModal = ({ integrationName, result, onClose }: OrdatConnectionTestModalProps) => {
    const { t } = useTranslation('integrations');

    const capabilities = parseJsonStringObject(result.capabilities);
    const checkedAt = capabilities[CAPABILITY_CHECKED_AT_KEY];

    const details = [
        { label: t('ordat.connectionTest.fossCompanyName'), value: result.fossCompanyName },
        { label: t('ordat.connectionTest.fossRelease'), value: result.fossRelease },
        { label: t('ordat.connectionTest.businessLine'), value: result.businessLine },
        { label: t('ordat.connectionTest.businessSubLine'), value: result.businessSubLine },
        { label: t('ordat.connectionTest.checkedAt'), value: checkedAt },
    ].filter((detail) => detail.value !== null && detail.value !== undefined && detail.value !== '');

    return (
        <Modal
            title={t('ordat.connectionTest.title', { name: integrationName })}
            onClose={onClose}
            buttons={<Button onClick={onClose}>{t('ordat.connectionTest.closeButton')}</Button>}
        >
            <div className="flex flex-col gap-4">
                <Alert severity={result.ok ? 'success' : 'error'}>
                    {result.ok ? t('ordat.connectionTest.ok') : t('ordat.connectionTest.failed')}
                </Alert>

                {result.errors?.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-content-danger-strong">
                            {t('ordat.connectionTest.errors')}
                        </span>
                        <ul className="list-disc pl-4 text-sm text-content-medium">
                            {result.errors.map((error) => (
                                <li key={error}>{error}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {result.warnings?.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-content-warning-strong">
                            {t('ordat.connectionTest.warnings')}
                        </span>
                        <ul className="list-disc pl-4 text-sm text-content-medium">
                            {result.warnings.map((warning) => (
                                <li key={warning}>{warning}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {details.length > 0 && (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                        {details.map((detail) => (
                            <div key={detail.label} className="contents">
                                <dt className="text-content-medium">{detail.label}</dt>
                                <dd className="text-content-strong">{String(detail.value)}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-content-strong">
                        {t('ordat.connectionTest.capabilities')}
                    </span>
                    <CapabilityList capabilities={capabilities} />
                </div>
            </div>
        </Modal>
    );
};

export default OrdatConnectionTestModal;
